import type { User } from '@web/figma-core/user/types'
import type { GoogleTokenType } from '@web/lib/google_auth_common'
import { z } from '@web/lib/zod/v4'
import {
  type AccountPickerData,
  type AppAuthAppType,
  type ArkoseAction,
  type Auth,
  AuthFormState,
  type SSOMethod,
} from '@web/redux/auth'
import type * as redux from 'redux'

export function isAuthFormState(value: string): value is AuthFormState {
  return !!value && Object.values<string>(AuthFormState).includes(value)
}

/**
 * Form states that are valid as the *initial* form state of a fresh auth page
 * load (e.g. via a `?form_state=` URL parameter or a server-rendered
 * `INITIAL_OPTIONS.form_state`).
 *
 * Notably this excludes `VERIFY_HUMAN`. The Arkose / "verify human" form is an
 * intermediate step that depends on prior form state — specifically, it
 * depends on `updateAuthEmailForArkose` having been called from the previous
 * form (sign-up, forgot-password, or Google SSO) to populate the Arkose API
 * param. Loading directly into `VERIFY_HUMAN` from a URL bypasses that setup
 * and produces an unhelpful captcha challenge with no pending action, so we
 * coerce these requests back to the default form (sign-up).
 */
export function isInitialAuthFormState(value: string): value is AuthFormState {
  return isAuthFormState(value) && value !== AuthFormState.VERIFY_HUMAN
}

/** Synced with USAGE_PURPOSE_VALUES constants in api_session.rb */
export enum UsagePurposeType {
  WORK = 'For work',
  PERSONAL = 'For personal use',
  EDU = 'For teaching or taking a class',
}

export enum AuthIframeType {
  MODAL = 'modal',
  FIXED_MODAL = 'fixedmodal',
  FOOTER = 'footer',
  INSERT = 'insert',
}
export interface AppAuth {
  id: string
  app_type: AppAuthAppType
}

/** Whether the referral link a visitor signed up through will apply. Set by GET /signup. */
export interface ReferralSignupStatusOption {
  eligible: boolean
  credit_amount: number
}

export interface AuthInitialOptions {
  form_state?: AuthFormState
  account_picker_data?: AccountPickerData
  auth_autofocus?: boolean
  auth_iframe_type?: AuthIframeType
  user_data?: User
  email?: string
  logged_out_email?: string
  error?: string
  name?: string
  email_token?: string
  redirect_url?: string
  app_auth_id?: string
  app_auth_app_type?: AppAuthAppType
  app_auth_g_secret: string
  app_auth_desktop_protocol?: string
  app_auth_users?: User[]
  sso_method?: SSOMethod
  org_name?: string
  existing_session: boolean
  should_use_redirect_instead?: boolean
  google_id_token?: string
  google_token_type?: GoogleTokenType
  origin?: string
  from_ms_teams?: boolean
  from_chrome_extension?: boolean
  from_embed?: boolean
  weavy?: boolean
  revoke_grantor?: boolean
  phone_number?: string
  sms_okay?: boolean
  saml_session_id?: string
  app_auth_msft_teams_url?: string
  ld_flag_ext_enforce_figma_apps?: boolean
  referral_status?: ReferralSignupStatusOption
}

export type HasAuthForEmailInput = {
  auth: Pick<Auth, 'email' | 'formState' | 'clickedSAMLSignIn' | 'invalidInput'>
  dispatch: redux.Dispatch
}

export interface TeamJoinLinkInitialOptions {
  team_name?: string
  token?: string
  attribution_param?: string
}

export interface FolderJoinLinkInitialOptions {
  token?: string
  attribution_param?: string
}

export const OPEN_SESSION_SIGNUP_LOCAL_STORAGE_KEY = 'signed_up_from_open_sessions'

export type AuthPage = {
  auth: Auth
}

export type ShowAuthFormPayload = (
  | {
      formState: AuthFormState.VERIFY_HUMAN
      prevState: AuthFormState.SIGN_IN | AuthFormState.SIGN_UP | AuthFormState.FORGOT_PASSWORD
      arkoseAction: ArkoseAction
    }
  | {
      formState: Exclude<AuthFormState, AuthFormState.VERIFY_HUMAN>
    }
) & { errorMessage?: string; userId?: string }

export type AuthEvent =
  // Google SSO
  | 'google_signup_error'
  | 'google_sso_login_redirect_attempt'
  | 'google_sso_attempt'
  | 'google_sso_success'
  | 'google_sso_attempt_skip_extra_info'

  // Google One Tap
  | 'google_one_tap_login_start'
  | 'google_one_tap_login_complete'
  | 'google_one_tap_prompt_moment'
  | 'google_one_tap_prompt_displayed'
  | 'google_one_tap_prompt_display_failed'
  | 'google_one_tap_prompt_skipped'
  | 'google_one_tap_prompt_dismissed'
  | 'google_one_tap_login_error'

  // SMS Recovery
  | 'sms_recover_attempt'
  | 'sms_recover_success'

  // Sign Up
  | 'show_auth_page'
  | 'sign_up_go_back'
  | 'sign_up_attempt'
  | 'sign_up_success'
  | 'signup_attempt_form_error'
  | 'email_start_submit'

  // Sign In
  | 'sign_in_attempt'
  | 'sign_in_success'

  // Password Reset
  | 'send_reset_password_email_attempt'
  | 'send_reset_password_email_success'
  | 'reset_password_attempt'
  | 'reset_password_success'

  // Magic Link
  | 'magic_link_request_success'

  // SAML
  | 'saml_redirect_to_provider_attempt'
  | 'saml_redirect_to_provider_success'
  | 'saml_redirect_to_magic_link'
  | 'saml_gate_redirect_attempt'
  | 'saml_gate_redirect_success'
  | 'saml_picker_rendered'
  | 'saml_picker_options_fetch_failed'

  // Email Only
  | 'email_only_attempt'
  | 'email_only_success'

  // Validation
  | 'validate_code_attempt'
  | 'resend_code'
  | 'send_validation_email_attempt'
  | 'send_validation_email_success'
  | 'validate_email_poll_error'
  | 'validate_email_poll_session_lost'

  // Arkose
  | 'captcha_challenge_shown'
  | 'arkose_token_error_signup'
  | 'arkose_token_error_forgot'
  | 'arkose_token_error_login'

  // Misc
  | 'error'
  | 'change_form_state'

const AuthErrorSamlRequiredSchema = z
  .object({
    reason: z.object({
      missing: z.literal('saml_required'),
      email: z.string(),
      redirect_url: z.string().nullish(),
    }),
  })
  .passthrough() // Allow other arbitrary properties at root of response data

export type AuthErrorSamlRequired = z.infer<typeof AuthErrorSamlRequiredSchema>
export function isAuthErrorSamlRequired(data: unknown): data is AuthErrorSamlRequired {
  return AuthErrorSamlRequiredSchema.safeParse(data).success
}
